import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Check, Lock, CheckCircle, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import Base from '../Base';

const AuthPagesForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    login: {
      title: 'Welcome Back',
      subtitle: 'Sign in to your account',
      showRememberMe: true,
      showForgotPassword: true,
      showSocialLogin: false,
      redirectAfterLogin: '/account'
    },
    signup: {
      title: 'Create Account',
      subtitle: 'Join us today',
      requireEmailVerification: true,
      showTermsCheckbox: true,
      termsText: 'I agree to the Terms & Conditions',
      termsLink: '/terms',
      showNewsletterOptIn: true,
      newsletterText: 'Subscribe to our newsletter',
      redirectAfterSignup: '/account'
    },
    socialLogin: {
      enableGoogle: false,
      enableFacebook: false,
      enableApple: false
    },
    passwordRequirements: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false
    }
  });

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/auth`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      if (response.data.content) {
        setFormData(response.data.content);
      }
    } catch (error) {
      console.error('Error loading auth pages data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/template/${templateId}/page/auth/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: formData
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your authentication pages have been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication pages...</p>
        </div>
      </div>
    } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/template-builder/${templateId}`)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Login & Signup Pages</h1>
                {lastSaved && <p className="text-xs text-gray-500">Last saved {lastSaved.toLocaleTimeString()}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button onClick={() => handleSave(false)} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark disabled:opacity-50">
                {saving ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Saving...</span></> : <><Check className="w-4 h-4" /><span>Save & Complete</span></>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Login Page */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D45614]-light rounded-lg flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Login Page</h2>
                <p className="text-sm text-gray-600">Configure the login experience</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
                <input type="text" value={formData.login.title} onChange={(e) => setFormData(prev => ({ ...prev, login: { ...prev.login, title: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                <input type="text" value={formData.login.subtitle} onChange={(e) => setFormData(prev => ({ ...prev, login: { ...prev.login, subtitle: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>

              <div className="space-y-3">
                {[
                  { id: 'showRememberMe', label: 'Show "Remember Me" Checkbox', path: 'login' },
                  { id: 'showForgotPassword', label: 'Show "Forgot Password" Link', path: 'login' },
                  { id: 'showSocialLogin', label: 'Enable Social Login Options', path: 'login' }
                ].map(feature => (
                  <div key={feature.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                    <input type="checkbox" id={feature.id} checked={formData[feature.path][feature.id]} onChange={(e) => setFormData(prev => ({ ...prev, [feature.path]: { ...prev[feature.path], [feature.id]: e.target.checked } }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                    <label htmlFor={feature.id} className="text-sm font-medium text-gray-700">{feature.label}</label>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Redirect After Login</label>
                <input type="text" value={formData.login.redirectAfterLogin} onChange={(e) => setFormData(prev => ({ ...prev, login: { ...prev.login, redirectAfterLogin: e.target.value } }))} placeholder="/account" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>
            </div>
          </div>

          {/* Signup Page */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Signup Page</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
                <input type="text" value={formData.signup.title} onChange={(e) => setFormData(prev => ({ ...prev, signup: { ...prev.signup, title: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                <input type="text" value={formData.signup.subtitle} onChange={(e) => setFormData(prev => ({ ...prev, signup: { ...prev.signup, subtitle: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>

              <div className="space-y-3">
                {[
                  { id: 'requireEmailVerification', label: 'Require Email Verification' },
                  { id: 'showTermsCheckbox', label: 'Show Terms & Conditions Checkbox' },
                  { id: 'showNewsletterOptIn', label: 'Show Newsletter Opt-in' }
                ].map(feature => (
                  <div key={feature.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                    <input type="checkbox" id={feature.id} checked={formData.signup[feature.id]} onChange={(e) => setFormData(prev => ({ ...prev, signup: { ...prev.signup, [feature.id]: e.target.checked } }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                    <label htmlFor={feature.id} className="text-sm font-medium text-gray-700">{feature.label}</label>
                  </div>
                ))}
              </div>

              {formData.signup.showTermsCheckbox && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Terms Checkbox Text</label>
                    <input type="text" value={formData.signup.termsText} onChange={(e) => setFormData(prev => ({ ...prev, signup: { ...prev.signup, termsText: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Terms Page Link</label>
                    <input type="text" value={formData.signup.termsLink} onChange={(e) => setFormData(prev => ({ ...prev, signup: { ...prev.signup, termsLink: e.target.value } }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Social Login */}
          {formData.login.showSocialLogin && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Social Login Options</h3>
              <div className="space-y-3">
                {[
                  { id: 'enableGoogle', label: 'Enable Google Login' },
                  { id: 'enableFacebook', label: 'Enable Facebook Login' },
                  { id: 'enableApple', label: 'Enable Apple Login' }
                ].map(feature => (
                  <div key={feature.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                    <input type="checkbox" id={feature.id} checked={formData.socialLogin[feature.id]} onChange={(e) => setFormData(prev => ({ ...prev, socialLogin: { ...prev.socialLogin, [feature.id]: e.target.checked } }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                    <label htmlFor={feature.id} className="text-sm font-medium text-gray-700">{feature.label}</label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Password Requirements */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Password Requirements</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Length</label>
                <input type="number" value={formData.passwordRequirements.minLength} onChange={(e) => setFormData(prev => ({ ...prev, passwordRequirements: { ...prev.passwordRequirements, minLength: parseInt(e.target.value) } }))} min="6" max="20" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-3">
                {[
                  { id: 'requireUppercase', label: 'Require Uppercase Letter' },
                  { id: 'requireLowercase', label: 'Require Lowercase Letter' },
                  { id: 'requireNumbers', label: 'Require Numbers' },
                  { id: 'requireSpecialChars', label: 'Require Special Characters' }
                ].map(feature => (
                  <div key={feature.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                    <input type="checkbox" id={feature.id} checked={formData.passwordRequirements[feature.id]} onChange={(e) => setFormData(prev => ({ ...prev, passwordRequirements: { ...prev.passwordRequirements, [feature.id]: e.target.checked } }))} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                    <label htmlFor={feature.id} className="text-sm font-medium text-gray-700">{feature.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default AuthPagesForm;
